<template>
  <div class="col-lg-10 mx-auto">
    <header class="text-center">
      <workflow-navigation workflowStage="You" />

      <h2 class="skin-page-heading">
          Your retirement plan
      </h2>

      <p v-if="showSubHeader" class="px3 mt-4 mb-5">
        Please enter your personal details along with your retirement date.
      </p>

        <p v-if="!showSubHeader" class="px3 mt-4 mb-5">
          Here are your personal details and chosen retirement date.
        </p>
    </header>

    <sub-section title="About you" :showHelp="false" mdColCount="7" mbSize="2">
      <div>
        <template v-if="!canEditBirthdayAndSex">
          <div class="row mt-3">
            <div class="col-md-4 col-6">
              <p><strong>Date of Birth:</strong></p>
            </div>
            <div class="col-md-8 col-6">
                <p>{{ dateOfBirthText }}</p>
            </div>
          </div>
          <div class="row mb-0">
            <div class="col-md-4 col-6">
              <p><strong>Sex:</strong></p>
            </div>
            <div class="col-md-8 col-6">
                <p>{{ sexText }}</p>
            </div>
          </div>
        </template>
        <template v-else>
            <div class="row">
                <div class="col-xl-6 mb-4">
                    <label for="exampleInputEmail1" class="form-label">Date of Birth:</label>
                    <vue-date-picker
                      v-model="dateOfBirthModel"
                      placeholder="Date of Birth"
                      format="d-MMM-yyyy"
                      :min-date="new Date(new Date().getFullYear() - 100, 0, 1)"
                      :max-date="new Date(new Date().getFullYear() - 20, 11, 31)"
                      :start-date="new Date(new Date().getFullYear() - 55, 0, 1)"
                      :enable-time-picker="false"
                      auto-apply
                      no-today
                      :aria-labels="{ input: 'Select your date of birth' }"
                    />
                </div>

                <div class="col-xl-6 mb-4">
                    <label for="sex" class="form-label w-100">Sex:</label>
                    <div class="btn-group d-flex" role="group" aria-label="Sex">
                        <radio-button v-for="(item, i) in getSexItems()"
                                      v-bind:key="i"
                                      name="sex"
                                      class="flex-fill"
                                      :label="item.text"
                                      :value="sexModel"
                                      @change="onChangeSex" />
                    </div>
                </div>
            </div>
           

            <template v-if="showOverFiftyFive">
                <div class="mb-4">
                    <p>Have you already started taking pension or cash from your pension pots?</p>
                    <div class="btn-group" role="group" aria-label="Sex">
                        <div class="btn-group w100"
                             role="group"
                             aria-label="Are you already taking pension or cash from your pension pots?">
                            <radio-button v-for="(item, i) in getYesNoItems()"
                                          v-bind:key="i"
                                          name="alreadyTakingPension"
                                          :label="item.text"
                                          :value="alreadyTakingPensionModel"
                                          @change="onChangeTakingPension" />
                        </div>
                    </div>
                </div>
            </template>
        </template>

        <div v-if="showRetirementDateAdvice" id="retirementDateAdvice" class="mb0">
            <p>{{ retirementDateAdviceText }}</p>
        </div>

        <template v-if="mustConfirmRetired">

            <p>
                You previously told us you wanted to start taking cash or
                pension from your pension pots in {{ retirementDateText }}, have
                you started doing so?
            </p>

          <div class="row">
            <div class="col-4">
              <div
                class="btn-group w-100"
                role="group"
                aria-label="Are you now retired?"
              >
                <radio-button
                  v-for="(item, i) in getYesNoItems()"
                  v-bind:key="i"
                  name="confirmRetired"
                  :label="item.text"
                  :value="confirmRetiredModel"
                  @change="onConfirmRetired"
                />
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="retirementDateIsInThePast">
          <div class="row">
            <div class="col-md-4 col-6">
              <p><strong>State Pension Age:</strong></p>
            </div>
            <div class="col-md-8 col-6">
              <p>{{ statePensionInfoTenseless }}</p>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <p>
                You have already started accessing benefits, we will build you a
                plan for the future, which will update each month.
              </p>
            </div>
          </div>
        </template>
        <template v-else>
          <div v-if="retirementDateIsReadOnly" class="row mt-2 mb-4">
            <div class="col-md-4 col-6">
              <p><strong>Retirement Date:</strong></p>
            </div>
            <div class="col-md-8 col-6">
              <p>{{ retirementDateText }}</p>
            </div>
          </div>

          <div v-show="!retirementDateIsReadOnly" class="row mb4" aria-label="Retirement date" aria-labelledby="retirementDateAdvice">
            <label class="form-label">Retirement Date:</label>
            <month-and-year
              ref="retirementDate"
              monthLabel="Month"
              yearLabel="Year"
              ariaMonthLabel="Retirement date month"
              ariaYearLabel="Retirement date year"
              @change="onRetirementDateChange"
            />
          </div>

          <div v-if="showAgeAtRetirement" class="row">
            <div class="col">
              <p class="mt-4">{{ ageAtRetirementText }}</p>
            </div>
          </div>
          <div v-if="showValidationMessage" class="row">
            <div class="col">
              <p class="warningMessage mt-4">{{ validationMessage }}</p>
            </div>
          </div>
        </template>
      </div>
    </sub-section>

    <div class="col-lg-7 mx-auto">
      <page-nav :showBack="false" @nextClicked="onNextClicked" :parent-will-handle-next="true" :next-link="nextLink" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import RadioButton from "@/shared/components/RadioButton.vue";
import MonthAndYear from "@/guiide/components/common/MonthAndYear.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import { staticData } from "@/guiide/ts/staticData";
import api from "../ts/api/api";
import { StatePensionDate } from "@/guiide/ts/statePensionDate";
import { formatter } from "@/shared/ts/formatter";
import { app } from "../ts/app";
import Temporal from "@/shared/ts/temporal";
import View from "../ts/view";
import { workflow } from "../ts/workflow/workflow";
import Result from "../ts/result";

export default defineComponent({
  name: "Dashboard",
  components: {
    WorkflowNavigation,
    Help,
    SubSection,
    VueDatePicker,
    RadioButton,
    MonthAndYear,
    PageNav
  },
  data() {
    return {
      noPotsWarningShown: false,
      statePensionDate: null as StatePensionDate,
      retireeLoadedDataFlag: false,
      retireeMissingDOB: false,
      retirementDateDefinedFlag: false,
      validationMessage: "",
      retirementDateIsInThePast: false,
      mustConfirmRetired: false
    };
  },
  setup() {
    const userCode = ref(null);
    const noPotsModal = ref(null);
    const dateOfBirthModel = ref(null);
    const sexModel = ref(null);
    const alreadyTakingPensionModel = ref("");
    const confirmRetiredModel = ref("Yes");
    const retirementDate = ref(null);
    const nextLink = ref("");

    return {
      userCode,
      noPotsModal,
      HelpText,
      dateOfBirthModel,
      sexModel,
      alreadyTakingPensionModel,
      confirmRetiredModel,
      retirementDate,
      nextLink
    };
  },
  async mounted() {
    this.retirementDate.setSelectedValue("", "");

    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.You);
    this.nextLink = nextWorkflowStep.resource;
  },
  watch: {
    async dateOfBirthModel() {
      await this.getStatePensionDates();
      this.checkRetirementYearOptions();
    },
    alreadyTakingPensionModel() {
      this.checkRetirementYearOptions();
    },
    retireeDateOfBirth(newValue) {
      this.retireeLoadedDataFlag = true;
      this.retireeMissingDOB = newValue == null;
      this.dateOfBirthModel = newValue == null ? null : new Date(newValue);

      if (!this.canEditBirthdayAndSex && this.statePensionDate == null) {
        this.getStatePensionDates();
      }
    },
    retireeSex(newValue) {
      this.sexModel = newValue;
      if (!this.canEditBirthdayAndSex && this.statePensionDate == null) {
        this.getStatePensionDates();
      }
    },
    retireeAlreadyTakingPension(newValue) {
      if (this.retireeDateOfBirth != null) {
        this.alreadyTakingPensionModel = newValue == true ? "Yes" : "No";
      }
    },
    retireeRetirementDate(newValue: string) {
      if (newValue == null) {
        this.retirementDateDefinedFlag = false;
        return;
      }

      const retirementDate = new Date(newValue);
      const month = (retirementDate.getMonth() + 1).toString();
      const year = retirementDate.getFullYear().toString();
      this.retirementDate.setSelectedValue(month, year);

      this.retirementDateIsInThePast = retirementDate < new Date();
      this.mustConfirmRetired =
        this.retirementDateIsInThePast && !this.retireeAlreadyTakingPension;
      this.retirementDateDefinedFlag = true;
    }
  },
  computed: {
    headerText: function() {
      return this.canEditBirthdayAndSex
        ? "Let's find out more about you"
        : "About you";
    },
    showSubHeader: function(): boolean {
      return this.canEditBirthdayAndSex;
    },
    ageAtRetirementText: function() {
      if (!this.showAgeAtRetirement) {
        return "";
      }

      const retirementDate = this.getRetirementDate();

      if (retirementDate == null) {
        return "";
      }

      if (this.dateOfBirthModel >= retirementDate) {
        return "You can't be born retired unfortunately!";
      }

      if (this.mustSetAndDisableRetirementDate()) {
        const currentAge = Temporal.calcAgeAtDate(
          this.dateOfBirthModel,
          new Date()
        );
        return `You are currently aged ${currentAge}.`;
      }

      const age = this.ageAtRetirement;
      return `You will be ${age} years old on your retirement date.`;
    },
    ageAtRetirement: function(): null | number {
      if (!this.showAgeAtRetirement) {
        return null;
      }

      const retirementDate = this.getRetirementDate();
      return Temporal.calcAgeAtDate(this.dateOfBirthModel, retirementDate);
    },
    dateOfBirthText: function() {
      return this.dateOfBirthModel == null
        ? ""
        : formatter.longDate(this.dateOfBirthModel);
    },
    dateWhenFiftyFiveText: function() {
      const dateAt55 = this.calcDateWhenFiftyFive();

      if (dateAt55 == null) {
        return "";
      }

      return `(the ${formatter.longDate(dateAt55)}) `;
    },
    sexText: function() {
      if (this.sexModel == null) {
        return "";
      }
      return this.sexModel == "M" ? "Male" : "Female";
    },
    retirementDateAdviceText: function() {
      const getLowerBoundText = () => {
        const dateAt55 = this.calcDateWhenFiftyFive();

        if (dateAt55 == null || dateAt55 < new Date()) {
          const earliestRetirementDate = this.calcEarliestRetirementDate();
          return formatter.monthAndYearDate(earliestRetirementDate);
        } else {
          return `age 55 ${this.dateWhenFiftyFiveText}`;
        }
      };

      if (this.retirementDateIsReadOnly) {
        return "";
      }

      const alreadyTakingPension = this.alreadyTakingPensionModel == "Yes";
      const statePensionInPayment = this.statePensionAlreadyInPayment === true;

      if (this.canEditBirthdayAndSex) {
        if (!this.dateOfBirthDefined) {
          return "";
        }

        const defaultAdvice = `Please choose a date between ${getLowerBoundText()} and your State Pension age ${
          this.statePensionInfo
        } for when you want to retire.`;

        if (this.showUnderFiftyFive) {
          return defaultAdvice;
        } else {
          if (alreadyTakingPension && statePensionInPayment) {
            return `As you have already taken some cash or income from your pension pots and you are past your State Pension age we will build you a plan starting straight away.`;
          } else if (alreadyTakingPension) {
            const statePensionEntitledOnText = this.getStatePensionEntitledOnText();
            return `As you have already taken some cash or income from your pension pots we will build you a plan starting straight away. ${statePensionEntitledOnText}`;
          } else if (statePensionInPayment) {
            return `As you are past your State Pension age we will build you a plan starting straight away.`;
          } else {
            return defaultAdvice;
          }
        }
      } else {
        if (alreadyTakingPension && statePensionInPayment) {
          return `You are past your State Pension age, and you have already taken some cash or income from your pension pots.`;
        } else if (alreadyTakingPension) {
          const statePensionEntitledOnText = this.getStatePensionEntitledOnText();
          return `You have already taken some cash or income from your pension pots. ${statePensionEntitledOnText}`;
        } else if (statePensionInPayment) {
          return `You are past your State Pension age.`;
        } else {
          return `You can adjust your retirement date if you wish. It should be between ${getLowerBoundText()} and your State Pension age ${
            this.statePensionInfo
          }.`;
        }
      }
    },
    canEditBirthdayAndSex: function() {
      return this.retireeLoadedDataFlag && this.retireeMissingDOB;
    },
    retirementDateIsReadOnly: function() {
      return this.$store.state.configuration.retirementDateReadOnly;
    },
    retireeDateOfBirth: function() {
      return this.$store.state.retiree?.dateOfBirth;
    },
    retireeSex: function() {
      return this.$store.state.retiree?.sex;
    },
    retireeAlreadyTakingPension: function() {
      return this.$store.state.retiree?.alreadyDrawingPension;
    },
    retireeRetirementDate: function() {
      return this.$store.state.retiree?.retirementDate;
    },
    retirementDateText: function(): string {
      if (this.retirementDateIsReadOnly) {
        if (this.retireeRetirementDate == null) {
          return "";
        }
        return formatter.monthAndYearDate(new Date(this.retireeRetirementDate));
      }

      const retirementDate = this.getRetirementDate();

      if (retirementDate == null) {
        return "";
      }

      return formatter.monthAndYearDate(retirementDate);
    },
    dateOfBirthDefined: function() {
      return this.dateOfBirthModel != null;
    },
    showUnderFiftyFive: function() {
      return (
        this.dateOfBirthModel != null &&
        this.dateOfBirthModel >=
          new Date().setFullYear(new Date().getFullYear() - 55)
      );
    },
    showOverFiftyFive: function() {
      return (
        this.dateOfBirthModel != null &&
        this.dateOfBirthModel <
          new Date().setFullYear(new Date().getFullYear() - 55)
      );
    },
    showRetirementDateAdvice: function() {
      return this.dateOfBirthDefined && !this.retirementDateIsInThePast && this.retirementDateAdviceText != "";
    },
    showAgeAtRetirement: function(): boolean {
      return this.dateOfBirthModel != null && this.retirementDateDefinedFlag;
    },
    statePensionInfoRecovered: function(): boolean {
      return this.statePensionDate != null;
    },
    showStatePensionInfo: function(): boolean {
      return this.statePensionDate != null;
    },
    showValidationMessage: function(): boolean {
      return this.validationMessage != "" && !this.retirementDateIsReadOnly;
    },
    statePensionInfo: function(): string {
      if (this.statePensionDate == null) {
        return "";
      }

      const formattedDate = formatter.longDate(
        this.statePensionDate.StatePensionDate
      );

      let tense = "at";
      const usePastTense = this.statePensionDate.StatePensionDate < new Date();
      if (this.canEditBirthdayAndSex) {
        if (usePastTense) {
          tense = "when you were";
        } else {
          tense = "of";
        }
      } else {
        if (usePastTense) {
          tense = "at";
        } else {
          tense = "of";
        }
      }

      return `${tense} ${this.statePensionDate.SPA} (on the ${formattedDate})`;
    },
    statePensionInfoTenseless: function(): string {
      if (this.statePensionDate == null) {
        return null;
      }

      const formattedDate = formatter.longDate(
        this.statePensionDate.StatePensionDate
      );

      return `${this.statePensionDate.SPA} (on the ${formattedDate})`;
    },
    statePensionAlreadyInPayment: function(): boolean | null {
      if (!this.statePensionInfoRecovered) {
        return null;
      }
      const now = new Date();
      return this.statePensionDate.StatePensionDate <= now;
    }
  },
  methods: {
    async onNextClicked() {
      if (this.retirementDateIsReadOnly) {
        await workflow.goToNextWorkflowStep();
      }

      if (this.mustConfirmRetired) {
        const isNowRetired = this.confirmRetiredModel == "Yes" ? true : false;
        const result = await this.updatePersonalDetail(isNowRetired, new Date(this.retireeRetirementDate));

        if (result.successful) {
          if (isNowRetired) {
            await workflow.goToNextWorkflowStep();
          } else {
            this.retirementDateIsInThePast = false;
            this.mustConfirmRetired = false;
            await app.getRetiree().then(() => {
              this.$forceUpdate();
              this.checkRetirementYearOptions();
            });
          }
        } else {
          this.validationMessage = result.message;
        }

        return;
      }

      if (this.retirementDateIsInThePast) {
        window.location.replace("/lifeexpectancy");
        return;
      }

      if (this.canEditBirthdayAndSex) {
        if (this.dateOfBirthModel == null) {
          this.validationMessage = "Please define your date of birth.";
          return;
        }

        if (this.sexModel == null || this.sexModel == "") {
          this.validationMessage = "Please define your sex.";
          return;
        }

        if (this.showOverFiftyFive && this.alreadyTakingPensionModel == "") {
          this.validationMessage =
            "Please tell us if you are already taking from your pension pots.";
          return;
        }
      }

      const retirementDate: Date = this.getRetirementDate();

      if (retirementDate == null) {
        this.validationMessage =
          "Please tell us when you would like to retire.";
        return;
      }

      const earliestRetirementDate = this.calcEarliestRetirementDate();
      if (retirementDate < earliestRetirementDate) {
        const monthAndYear = formatter.monthAndYearDate(earliestRetirementDate);
        this.validationMessage = `The earliest retirement date you can choose is ${monthAndYear}.`;
        return;
      }

      const dateAt55 = this.calcDateWhenFiftyFive();
      if (retirementDate < dateAt55) {
        this.validationMessage =
          "Choose a retirement date when you will be at least 55 years old.";
        return;
      }

      const statePensionDateIsInFuture =
        this.statePensionDate.StatePensionDate > new Date();
      if (
        statePensionDateIsInFuture &&
        retirementDate > this.statePensionDate.StatePensionDate
      ) {
        this.validationMessage =
          "Choose a retirement date that is before your State Pension age.";
        return;
      }

      this.validationMessage = "";

      let updateRequired = true;
      if (!this.canEditBirthdayAndSex) {
        const originalRetireeDate = new Date(this.retireeRetirementDate);
        updateRequired =
          retirementDate.toLocaleDateString() !=
          originalRetireeDate.toLocaleDateString();
      }

      let result = new Result(true);
      const isAlreadyTakingPension =
        this.alreadyTakingPensionModel == "Yes" ? true : false;

      if (updateRequired) {
        result = await this.updatePersonalDetail(
          isAlreadyTakingPension,
          retirementDate
        );
      }

      if (result.successful) {
        await workflow.goToNextWorkflowStep();
      } else {
        this.validationMessage = result.message;
      }
    },
    async updatePersonalDetail(
      isAlreadyTakingPension: boolean,
      retirementDate: Date
    ): Promise<Result> {
      return await app.updatePersonalDetail(
        this.dateOfBirthModel,
        this.sexModel,
        isAlreadyTakingPension,
        retirementDate
      );
    },
    getRetirementDate(): Date | null {
      if (this.retirementDateIsInThePast) {
        return new Date(this.retireeRetirementDate);
      }

      if (this.retirementDate == null) {
        return null;
      }

      return this.retirementDate.getSelectedDate();
    },
    calcDateWhenFiftyFive(): Date | null {
      if (this.dateOfBirthModel == null) {
        return null;
      }

      const dateAt55 = new Date(this.dateOfBirthModel);
      let year = dateAt55.getFullYear();
      year += 55;
      dateAt55.setFullYear(year);

      return dateAt55;
    },
    async onChangeSex(newValue: string) {
      this.sexModel = newValue;
      await this.getStatePensionDates();
      this.checkRetirementYearOptions();
    },
    onChangeTakingPension(newValue: string) {
      this.alreadyTakingPensionModel = newValue;
    },
    onConfirmRetired(newValue: string) {
      this.confirmRetiredModel = newValue;
    },
    onRetirementDateChange() {
      const retirementDate = this.getRetirementDate();

      if (retirementDate == null) {
        this.retirementDateDefinedFlag = false;
        return;
      }

      this.retirementDateDefinedFlag = true;
    },
    getFunc() {
      return null;
    },
    getSexItems() {
      return staticData.SexData;
    },
    getYesNoItems() {
      return staticData.YesNoData;
    },
    calcEarliestRetirementDate(): Date {
      return Temporal.getFirstOfNextMonth();
    },
    getStatePensionEntitledOnText(): string {
      if (this.statePensionDate == null) {
        return "";
      }

      return `You will be entitled to your State Pension at the age ${this.statePensionInfo}.`;
    },
    checkRetirementYearOptions() {
      if (this.retirementDateIsInThePast) {
        return;
      }

      const currentYear = new Date().getFullYear();

      if (this.dateOfBirthModel == null) {
        this.retirementDate.setFirstAndLastYear(currentYear, currentYear + 15);
        return;
      }

      if (this.mustSetAndDisableRetirementDate()) {
        const defaultRetirementDate = this.calcEarliestRetirementDate();

        const defaultRetirementYear = defaultRetirementDate.getFullYear();
        const defaultRetirementMonth = defaultRetirementDate.getMonth() + 1;

        this.retirementDate.setFirstAndLastYear(
          defaultRetirementYear,
          defaultRetirementYear
        );
        this.retirementDate.setSelectedValue(
          defaultRetirementMonth,
          defaultRetirementYear
        );
        this.retirementDate.setDisabledState(true);
        this.retirementDateDefinedFlag = true;
        return;
      }

      this.retirementDate.setDisabledState(false);

      const startYear = Math.max(
        currentYear,
        this.dateOfBirthModel.getFullYear() + 55
      );
      let endYear = startYear + 15;
      if (this.statePensionDate != null) {
        endYear = this.statePensionDate.StatePensionDate.getFullYear();
      }

      this.retirementDate.setFirstAndLastYear(startYear, endYear);

      const currentlySelectedDate = this.getRetirementDate();
      this.retirementDateDefinedFlag = currentlySelectedDate != null;
    },
    mustSetAndDisableRetirementDate() {
      if (this.showUnderFiftyFive) {
        return false;
      }

      if (this.alreadyTakingPensionModel == "Yes") {
        return true;
      }

      const statePensionAlreadyPaying = this.statePensionAlreadyInPayment;

      if (statePensionAlreadyPaying != null && statePensionAlreadyPaying) {
        return true;
      }

      return false;
    },
    async getStatePensionDates() {
      if (this.dateOfBirthModel == null) {
        return;
      }

      if (this.sexModel == null || this.sexModel == "") {
        return;
      }

      let sex = this.sexModel;
      if (sex == "Male") {
        sex = "M";
      } else if (sex == "Female") {
        sex = "F";
      }

      this.statePensionDate = await api.getStatePensionTemporal(
        this.dateOfBirthModel,
        sex
      );
    }
  }
});
</script>

<style scoped>
.questionText {
  margin-top: 18px;
  margin-bottom: 8px;
}

.warningMessage {
  color: #003d99 !important;
}
</style>
